import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { projectApi, taskApi } from "../../backend/client";
import { useCurrentUser } from "../../backend/queries/auth";
import { useProjectParticipants } from "../../backend/queries/projects";
import type { TaskAssignment } from "../../backend/api/generated/openapi";
import {
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Plus,
  Users,
} from "lucide-react";
import { ErrorState, StatusBadge } from "../../components/patterns";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import MeetingForm from "./meetingform";
import DocumentChecklistForm from "./documentchecklistform";
import TransfereeNotesForm from "./transfereenotesform";
import PropertiesForm from "./propertiesform";
import ECRLinkForm from "./ecrlinkform";
import LeaseLinkForm from "./leaselinkform";
import ProjectComments from "../projects/ProjectComments";
import UtilitiesForm from "./utilitiesform";
import AirportPickupForm from "./airportpickupform";
import InsuranceForm from "./insuranceform";
import HomeVisitForm from "./homevisitform";
import RubbishBookingForm from "./rubbishbookingform";
import BoxDeliveryForm from "./boxdeliveryform";
import ElevatorAccessForm from "./elevatoraccessform";
import BookCleanerForm from "./bookcleanerform";
import BookDeclutterForm from "./bookdeclutterform";
import QuoteDocForm from "./quotedocform";
import HotelTransferForm from "./hoteltransferform";

const safeParseJSON = (s) => {
  try {
    return JSON.parse(s && s.trim() ? s : "{}");
  } catch {
    return {};
  }
};

type AgentTasksProps = {
  projectId: string | number;
};

/** The project checklist shown inside the project workspace's Tasks view. */
const AgentTasks = ({ projectId }: AgentTasksProps) => {
  const paramProjectId = String(projectId);
  const [searchParams] = useSearchParams();
  const linkedTaskId = searchParams.get("task");
  const [projects, setProjects] = useState([]);
  const [selectProjectId, setSelectProjectId] = useState(null);
  const [selectProject, setSelectProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskAssignments, setTaskAssignments] = useState<TaskAssignment[]>([]);
  const [taskActionError, setTaskActionError] = useState("");
  const [assignmentBusy, setAssignmentBusy] = useState("");
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const focusedTaskId = useRef<string | null>(null);
  useEffect(() => {
    if (!linkedTaskId) {
      focusedTaskId.current = null;
      return;
    }
    if (loadingTasks || focusedTaskId.current === linkedTaskId) return;
    const target = document.getElementById(`task-${linkedTaskId}`);
    if (target) {
      focusedTaskId.current = linkedTaskId;
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: "center" });
    }
  }, [linkedTaskId, loadingTasks, tasks]);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const [addTaskError, setAddTaskError] = useState("");
  const [taskDraft, setTaskDraft] = useState({
    title: "",
    clientTitle: "",
    visibleToClient: true,
  });

  const [showTask5MeetingForm, setShowTask5MeetingForm] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);

  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [currentDocumentTaskId, setCurrentDocumentTaskId] = useState(null);

  const [showTransfereeNotesForm, setShowTransfereeNotesForm] = useState(false);
  const [currentNotesTaskId, setCurrentNotesTaskId] = useState(null);
  const [currentNotesInitial, setCurrentNotesInitial] = useState(null);

  const [showECRLinkForm, setShowECRLinkForm] = useState(false);
  const [currentECRTaskId, setCurrentECRTaskId] = useState(null);

  const [showPropertiesForm, setShowPropertiesForm] = useState(false);
  const [currentPropertiesTaskId, setCurrentPropertiesTaskId] = useState(null);

  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const [showLeaseLinkForm, setShowLeaseLinkForm] = useState(false);
  const [currentLeaseTaskId, setCurrentLeaseTaskId] = useState(null);

  const [showUtilitiesForm, setShowUtilitiesForm] = useState(false);
  const [currentUtilitiesTaskId, setCurrentUtilitiesTaskId] = useState(null);

  const [showAirportForm, setShowAirportForm] = useState(false);

  const [showInsuranceForm, setShowInsuranceForm] = useState(false);

  const [showHomeVisitForm, setShowHomeVisitForm] = useState(false);
  const [currentHomeVisitTaskId, setCurrentHomeVisitTaskId] = useState(null);

  const [showRubbishForm, setShowRubbishForm] = useState(false);
  const [currentRubbishTaskId, setCurrentRubbishTaskId] = useState(null);

  const [showBookDeclutterForm, setShowBookDeclutterForm] = useState(false);

  const [showQuoteDocForm, setShowQuoteDocForm] = useState(false);
  const [currentQuoteTaskId, setCurrentQuoteTaskId] = useState(null);

  const [showHotelTransferForm, setShowHotelTransferForm] = useState(false);
  const [currentHotelTransferTaskId, setCurrentHotelTransferTaskId] =
    useState(null);

  const { data: currentUser = null } = useCurrentUser();
  const currentRole = String(currentUser?.profile?.role || currentUser?.role || "");
  const canAssignTasks = (currentRole === "Owner" || currentRole === "Admin") || currentRole === "Agent";
  const participantsQuery = useProjectParticipants(
    String(selectProjectId || ""),
    canAssignTasks && Boolean(selectProjectId),
  );
  const projectStaff = (participantsQuery.data || []).filter(
    (participant) =>
      !participant.ends_at &&
      (participant.participant_role === "lead_agent" ||
        participant.participant_role === "supporting_agent"),
  );

  const [showBoxDeliveryForm, setShowBoxDeliveryForm] = useState(false);
  const [currentBoxDeliveryTaskId, setCurrentBoxDeliveryTaskId] =
    useState(null);

  const [showElevatorForm, setShowElevatorForm] = useState(false);
  const [currentElevatorTaskId, setCurrentElevatorTaskId] = useState(null);

  //book cleaner
  const [showBookCleanerForm, setShowBookCleanerForm] = useState(false);

  const handleStartEdit = (task) => {
    setEditingTaskId(task.id);
    setEditTitle(task.title);
  };

  const handleSaveEdit = async (taskId) => {
    if (!editTitle || !editTitle.trim()) return;

    const { data, error } = await taskApi.update(taskId, {
      title: editTitle.trim(),
    });

    // Leaves the title editable so the change is not lost.
    if (error) {
      setTaskActionError(error.message || "The task title could not be saved.");
      return;
    }
    setTaskActionError("");

    if (data) {
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, title: data.title } : t)),
      );
    }

    setEditingTaskId(null);
    setEditTitle("");
  };

  useEffect(() => {
    const fetchProjects = async () => {
      setLoadError("");

      // Fetched on its own: the list endpoint returns at most 100 projects,
      // so searching it can miss one the user can open.
      const { data, error } = await projectApi.get(paramProjectId);
      if (error) {
        setLoadError(
          error.status === 404
            ? "This project could not be found."
            : error.message || "The project could not be loaded.",
        );
        return;
      }
      setProjects(data ? [data] : []);
      setSelectProjectId(data?.id ?? null);
    };
    fetchProjects();
  }, [paramProjectId, reloadKey]);

  useEffect(() => {
    if (!selectProjectId) {
      setTasks([]);
      setTaskAssignments([]);
      setSelectProject(null);
      setLoadingTasks(false);
      return;
    }

    const handleProjectSetup = async (projectId) => {
      setLoadingTasks(true);
      const { error: setupError } = await taskApi.setupProjectTasks(projectId);

      if (setupError) {
        setLoadError(
          setupError.message || "The project checklist could not be prepared.",
        );
        setLoadingTasks(false);
        return;
      }

      await fetchProjectTasks(projectId);
    };

    const project = projects.find(
      (p) => String(p.id) === String(selectProjectId),
    );
    if (project) {
      setSelectProject(project);
      (async () => {
        await handleProjectSetup(project.id);
      })();
    }
  }, [selectProjectId, projects]);

  const fetchProjectTasks = async (projectId) => {
    setLoadingTasks(true);
    try {
      const [tasksResult, assignmentsResult] = await Promise.all([
        taskApi.listForProject(projectId, {
          order: "task_number",
          ascending: true,
        }),
        taskApi.listAssigneesForProject(projectId),
      ]);

      if (tasksResult.error) {
        setLoadError(tasksResult.error.message || "Tasks could not be loaded.");
        return;
      }
      setLoadError("");
      setTasks(tasksResult.data || []);
      setTaskAssignments(assignmentsResult.data || []);
      if (assignmentsResult.error) {
        setTaskActionError("Task owners could not be loaded.");
      }
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleTaskOwnerChange = async (taskId, userId, assigned) => {
    const busyKey = `${taskId}:${userId}`;
    setAssignmentBusy(busyKey);
    setTaskActionError("");
    const result = assigned
      ? await taskApi.unassign(taskId, userId)
      : await taskApi.assign(taskId, userId);
    if (result.error) {
      setTaskActionError(result.error.message);
    } else if (selectProjectId) {
      const { data } = await taskApi.listAssigneesForProject(selectProjectId);
      setTaskAssignments(data || []);
    }
    setAssignmentBusy("");
  };

  const handleCheckboxChange = async (taskId, currentValue) => {
    const { error } = await taskApi.updateStatus(
      taskId,
      !currentValue ? "complete" : "incomplete",
    );

    // Only tick the box once the API has accepted it, so a refused update
    // never looks like completed work.
    if (error) {
      setTaskActionError(error.message || "The task could not be updated.");
      return;
    }
    setTaskActionError("");

    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId ? { ...task, is_complete: !task.is_complete } : task,
      ),
    );
  };

  const handleAddTask = async () => {
    const title = taskDraft.title.trim();
    if (!selectProjectId || !title || addingTask) return;

    setAddingTask(true);
    setAddTaskError("");
    const nextTaskNumber =
      tasks.reduce(
        (highest, task) => Math.max(highest, Number(task.task_number) || 0),
        0,
      ) + 1;
    const { error } = await taskApi.create({
      project_id: Number(selectProjectId),
      task_number: nextTaskNumber,
      title,
      client_title: taskDraft.clientTitle.trim() || title,
      visible_to_client: taskDraft.visibleToClient,
    });

    if (error) {
      setAddTaskError(error.message || "Could not add this task.");
      setAddingTask(false);
      return;
    }

    await fetchProjectTasks(selectProjectId);
    setTaskDraft({ title: "", clientTitle: "", visibleToClient: true });
    setAddingTask(false);
    setAddTaskOpen(false);
  };

  const handleHideTaskLocal = async (taskId, isCurrentlyHidden) => {
    const nextHidden = !isCurrentlyHidden;
    const { error } = await taskApi.update(taskId, { is_hidden: nextHidden });
    if (error) {
      setTaskActionError(error.message || "The task could not be updated.");
      return;
    }
    setTaskActionError("");
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, is_hidden: nextHidden } : t)),
    );
  };

  const handlePropertiesSave = async (payload) => {
    try {
      let updated = null;
      if (payload && payload.id) updated = payload;
      else if (payload && payload.task && payload.task.id)
        updated = payload.task;
      else if (payload && payload.updatedTask && payload.updatedTask.id)
        updated = payload.updatedTask;
      else if (typeof payload === "number") updated = { id: payload };

      if (updated && updated.id) {
        setTasks((prev) =>
          prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)),
        );
      } else {
        await fetchProjectTasks(selectProject?.id ?? selectProjectId);
      }
    } finally {
      setShowPropertiesForm(false);
    }
  };

  let taskMessage = null;
  if (loadError) {
    taskMessage = "Tasks could not be loaded";
  } else if (loadingTasks) {
    taskMessage = "Loading project tasks";
  } else if (selectProjectId && tasks.length === 0) {
    taskMessage = "No tasks found for this project";
  } else if (!selectProjectId) {
    taskMessage = "Loading project tasks";
  }

  return (
    <>
      <div className="w-full">
        <div className="flex w-full flex-col gap-4">
          <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
            <Card className="rounded-xl border-black/5 shadow-sm">
              <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
                <div>
                  <CardTitle>Tasks</CardTitle>
                  <CardDescription>
                    {selectProjectId && tasks.length > 0
                      ? `${tasks.length} project tasks`
                      : taskMessage}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {loadingTasks && (
                    <Badge variant="outline" className="gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading
                    </Badge>
                  )}
                  {selectProjectId && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setAddTaskError("");
                        setAddTaskOpen(true);
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      Add task
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {taskActionError && (
                  <p role="alert" className="mb-3 text-sm text-destructive">
                    {taskActionError}
                  </p>
                )}
                {loadError ? (
                  <ErrorState
                    title="Tasks could not be loaded"
                    description={loadError}
                    onRetry={() => setReloadKey((key) => key + 1)}
                  />
                ) : loadingTasks ? (
                  <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading tasks
                  </div>
                ) : selectProjectId && tasks.length > 0 ? (
                  <div className="space-y-3">
                    {tasks.map((task) => {
                      const meetingDetails = safeParseJSON(
                        task.meeting_details,
                      );
                      const hasMeetingDetails =
                        meetingDetails &&
                        Object.keys(meetingDetails).length > 0;

                      const taskKind = task.task_kind || "general";

                      return (
                        <TaskItem
                          key={task.id}
                          task={task}
                          meetingDetails={meetingDetails}
                          hasMeetingDetails={hasMeetingDetails}
                          editing={editingTaskId === task.id}
                          editTitle={editTitle}
                          setEditTitle={setEditTitle}
                          onStartEdit={() => handleStartEdit(task)}
                          onSaveEdit={() => handleSaveEdit(task.id)}
                          onCancelEdit={() => {
                            setEditingTaskId(null);
                            setEditTitle("");
                          }}
                          onComplete={() =>
                            handleCheckboxChange(task.id, task.is_complete)
                          }
                          onHide={() =>
                            !task.is_complete &&
                            handleHideTaskLocal(task.id, task.is_hidden)
                          }
                          onPackageChange={async (field, value) => {
                            const { error } = await taskApi.update(task.id, { [field]: value });
                            if (error) {
                              setTaskActionError(
                                error.message || "The task could not be updated.",
                              );
                              return;
                            }
                            setTaskActionError("");
                            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, [field]: value } : t));
                          }}
                          staff={projectStaff}
                          assignments={taskAssignments.filter(
                            (assignment) => assignment.task_id === task.id,
                          )}
                          canAssign={canAssignTasks}
                          assignmentBusy={assignmentBusy}
                          onOwnerChange={(userId, assigned) =>
                            handleTaskOwnerChange(task.id, userId, assigned)
                          }
                          actions={
                            <>
                              {taskKind === "meeting_details" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentTaskId(task.id);
                                    setShowTask5MeetingForm(true);
                                  }}
                                >
                                  {hasMeetingDetails
                                    ? "Edit Meeting"
                                    : "Meeting Details"}
                                </TaskActionButton>
                              )}

                              {taskKind === "documents" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentDocumentTaskId(
                                      selectProject?.id ?? selectProjectId,
                                    );
                                    setShowDocumentForm(true);
                                  }}
                                >
                                  Documents
                                </TaskActionButton>
                              )}

                              {taskKind === "properties" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentPropertiesTaskId(task.id);
                                    setShowPropertiesForm(true);
                                  }}
                                >
                                  Properties
                                </TaskActionButton>
                              )}

                              {taskKind === "transferee_notes" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentNotesTaskId(task.id);
                                    setCurrentNotesInitial(
                                      task.transferee_notes ?? null,
                                    );
                                    setShowTransfereeNotesForm(true);
                                  }}
                                >
                                  Transferee Notes
                                </TaskActionButton>
                              )}

                              {taskKind === "ecr" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentECRTaskId(task.id);
                                    setShowECRLinkForm(true);
                                  }}
                                >
                                  ECR Link
                                </TaskActionButton>
                              )}

                              {taskKind === "lease" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentLeaseTaskId(task.id);
                                    setShowLeaseLinkForm(true);
                                  }}
                                >
                                  Lease Details
                                </TaskActionButton>
                              )}

                              {taskKind === "utilities" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentUtilitiesTaskId(task.id);
                                    setShowUtilitiesForm(true);
                                  }}
                                >
                                  Utilities
                                </TaskActionButton>
                              )}

                              {taskKind === "airport_pickup" && (
                                <TaskActionButton
                                  onClick={() => setShowAirportForm(true)}
                                >
                                  Airport Pickup
                                </TaskActionButton>
                              )}

                              {taskKind === "cleaner" && (
                                <TaskActionButton
                                  onClick={() => setShowBookCleanerForm(true)}
                                >
                                  Cleaner
                                </TaskActionButton>
                              )}

                              {taskKind === "declutter" && (
                                <TaskActionButton
                                  onClick={() => setShowBookDeclutterForm(true)}
                                >
                                  Declutter
                                </TaskActionButton>
                              )}

                              {taskKind === "quote" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentQuoteTaskId(task.id);
                                    setShowQuoteDocForm(true);
                                  }}
                                >
                                  Quote Document
                                </TaskActionButton>
                              )}

                              {taskKind === "insurance" && (
                                <TaskActionButton
                                  onClick={() => setShowInsuranceForm(true)}
                                >
                                  Insurance
                                </TaskActionButton>
                              )}

                              {taskKind === "rubbish" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentRubbishTaskId(task.id);
                                    setShowRubbishForm(true);
                                  }}
                                >
                                  Rubbish Booking
                                </TaskActionButton>
                              )}

                              {taskKind === "home_visit" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentHomeVisitTaskId(task.id);
                                    setShowHomeVisitForm(true);
                                  }}
                                >
                                  Home Visit
                                </TaskActionButton>
                              )}

                              {taskKind === "hotel_transfer" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentHotelTransferTaskId(task.id);
                                    setShowHotelTransferForm(true);
                                  }}
                                >
                                  Hotel Transfer
                                </TaskActionButton>
                              )}

                              {taskKind === "box_delivery" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentBoxDeliveryTaskId(task.id);
                                    setShowBoxDeliveryForm(true);
                                  }}
                                >
                                  Box Delivery
                                </TaskActionButton>
                              )}

                              {taskKind === "elevator_access" && (
                                <TaskActionButton
                                  onClick={() => {
                                    setCurrentElevatorTaskId(task.id);
                                    setShowElevatorForm(true);
                                  }}
                                >
                                  Elevator Access
                                </TaskActionButton>
                              )}
                            </>
                          }
                        />
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState text={taskMessage} />
                )}
              </CardContent>
            </Card>

            <Card className="rounded-xl border-black/5 shadow-sm">
              <CardHeader className="p-4">
                <CardTitle>Notes</CardTitle>
                <CardDescription>
                  Internal project conversation.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {selectProject ? (
                  <ProjectComments
                    projectId={Number(selectProject.id)}
                    currentUser={currentUser}
                  />
                ) : (
                  <EmptyState text="Notes appear once the project has loaded." />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={addTaskOpen} onOpenChange={setAddTaskOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add project task</DialogTitle>
            <DialogDescription>
              Add one task to this project only. Task defaults are managed
              separately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            {addTaskError && (
              <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {addTaskError}
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-project-task-title">Internal title</Label>
              <Input
                id="new-project-task-title"
                value={taskDraft.title}
                onChange={(event) =>
                  setTaskDraft((draft) => ({
                    ...draft,
                    title: event.target.value,
                  }))
                }
                placeholder="e.g. Confirm removal booking"
                disabled={addingTask}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-project-task-client-title">
                Client-facing title
              </Label>
              <Input
                id="new-project-task-client-title"
                value={taskDraft.clientTitle}
                onChange={(event) =>
                  setTaskDraft((draft) => ({
                    ...draft,
                    clientTitle: event.target.value,
                  }))
                }
                placeholder="Uses the internal title when blank"
                disabled={addingTask}
              />
            </div>
            <label className="flex items-center gap-3 rounded-md border p-3 text-sm font-medium">
              <Checkbox
                checked={taskDraft.visibleToClient}
                onCheckedChange={(checked) =>
                  setTaskDraft((draft) => ({
                    ...draft,
                    visibleToClient: checked === true,
                  }))
                }
                disabled={addingTask}
              />
              Visible to client
            </label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddTaskOpen(false)}
              disabled={addingTask}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleAddTask()}
              disabled={addingTask || !taskDraft.title.trim()}
            >
              {addingTask ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showTask5MeetingForm && (
        <MeetingForm
          taskId={currentTaskId}
          handleCloseForm={() => setShowTask5MeetingForm(false)}
          onSave={() => {
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
            setShowTask5MeetingForm(false);
          }}
        />
      )}

      {showDocumentForm && (
        <DocumentChecklistForm
          projectId={currentDocumentTaskId}
          onClose={() => setShowDocumentForm(false)}
          onSave={() => {
            setShowDocumentForm(false);
          }}
        />
      )}

      {showTransfereeNotesForm && (
        <TransfereeNotesForm
          taskId={currentNotesTaskId}
          initialNotes={currentNotesInitial}
          onClose={() => setShowTransfereeNotesForm(false)}
          onSave={() => {
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
            setShowTransfereeNotesForm(false);
          }}
        />
      )}

      {showECRLinkForm && (
        <ECRLinkForm
          taskId={currentECRTaskId}
          onClose={() => setShowECRLinkForm(false)}
        />
      )}

      {showPropertiesForm && (
        <PropertiesForm
          isOpen={showPropertiesForm}
          taskId={currentPropertiesTaskId}
          projectId={selectProject?.id ?? selectProjectId}
          onClose={() => setShowPropertiesForm(false)}
          onSave={handlePropertiesSave}
        />
      )}

      {showLeaseLinkForm && (
        <LeaseLinkForm
          taskId={currentLeaseTaskId}
          onClose={() => setShowLeaseLinkForm(false)}
        />
      )}

      {showUtilitiesForm && (
        <UtilitiesForm
          projectId={currentUtilitiesTaskId}
          onClose={() => setShowUtilitiesForm(false)}
          onSave={() => fetchProjectTasks(selectProject?.id ?? selectProjectId)}
        />
      )}

      {showAirportForm && (
        <AirportPickupForm
          projectId={selectProject?.id ?? selectProjectId}
          onClose={() => setShowAirportForm(false)}
          onSave={() => {
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
            setShowAirportForm(false);
          }}
        />
      )}

      {showInsuranceForm && (
        <InsuranceForm
          projectId={selectProject?.id ?? selectProjectId}
          onClose={() => setShowInsuranceForm(false)}
          onSave={() => {
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
            setShowInsuranceForm(false);
          }}
        />
      )}

      {showHomeVisitForm && (
        <HomeVisitForm
          taskId={currentHomeVisitTaskId}
          projectId={selectProject?.id ?? selectProjectId}
          onClose={() => setShowHomeVisitForm(false)}
          onSave={() => {
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
            setShowHomeVisitForm(false);
          }}
        />
      )}
      {showRubbishForm && (
        <RubbishBookingForm
          isOpen={showRubbishForm}
          onClose={() => setShowRubbishForm(false)}
          projectId={selectProject?.id ?? selectProjectId}
          taskId={currentRubbishTaskId}
          onSave={() => {
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
            setShowRubbishForm(false);
          }}
        />
      )}
      {showHotelTransferForm && (
        <HotelTransferForm
          isOpen={showHotelTransferForm}
          onClose={() => setShowHotelTransferForm(false)}
          projectId={selectProject?.id ?? selectProjectId}
          taskId={currentHotelTransferTaskId}
          onSave={() => {
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
            setShowHotelTransferForm(false);
          }}
        />
      )}
      {showBoxDeliveryForm && (
        <BoxDeliveryForm
          isOpen={showBoxDeliveryForm}
          onClose={() => setShowBoxDeliveryForm(false)}
          projectId={selectProject?.id ?? selectProjectId}
          taskId={currentBoxDeliveryTaskId}
          onSave={() => {
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
            setShowBoxDeliveryForm(false);
          }}
        />
      )}
      {showElevatorForm && (
        <ElevatorAccessForm
          isOpen={showElevatorForm}
          onClose={() => setShowElevatorForm(false)}
          projectId={selectProject?.id ?? selectProjectId}
          taskId={currentElevatorTaskId}
          onSave={() => {
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
            setShowElevatorForm(false);
          }}
        />
      )}

      {showBookCleanerForm && (
        <BookCleanerForm
          projectId={selectProject?.id ?? selectProjectId}
          onClose={() => setShowBookCleanerForm(false)}
          onSave={() => {
            setShowBookCleanerForm(false);
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
          }}
        />
      )}

      {showBookDeclutterForm && (
        <BookDeclutterForm
          projectId={selectProject?.id ?? selectProjectId}
          onClose={() => setShowBookDeclutterForm(false)}
          onSave={() => {
            setShowBookDeclutterForm(false);
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
          }}
        />
      )}

      {showQuoteDocForm && (
        <QuoteDocForm
          taskId={currentQuoteTaskId}
          onClose={() => setShowQuoteDocForm(false)}
          onSave={() => {
            setShowQuoteDocForm(false);
            fetchProjectTasks(selectProject?.id ?? selectProjectId);
          }}
        />
      )}
    </>
  );
};

const TaskItem = ({
  task,
  meetingDetails,
  hasMeetingDetails,
  editing,
  editTitle,
  setEditTitle,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onComplete,
  onHide,
  onPackageChange,
  staff,
  assignments,
  canAssign,
  assignmentBusy,
  onOwnerChange,
  actions,
}) => (
  <div
    id={`task-${task.id}`}
    tabIndex={-1}
    className={[
      "rounded-md border p-4 transition-colors",
      task.is_complete ? "border-primary/25 bg-primary/5" : "bg-card",
      task.is_hidden ? "opacity-55" : "",
    ].join(" ")}
  >
    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={!!task.is_complete}
            onCheckedChange={onComplete}
            disabled={!!task.is_hidden || task.included_in_package === false}
            aria-label="Toggle task completion"
            className="mt-1"
          />
          <div className="min-w-0 flex-1">
            {editing ? (
              <Input
                value={editTitle}
                onChange={(event) => setEditTitle(event.target.value)}
                onBlur={onSaveEdit}
                onKeyDown={(event) => {
                  if (event.key === "Enter") onSaveEdit();
                  if (event.key === "Escape") onCancelEdit();
                }}
                autoFocus
              />
            ) : (
              <button
                type="button"
                className={[
                  "block w-full break-words text-left text-base font-medium",
                  task.is_complete ? "text-muted-foreground line-through" : "",
                ].join(" ")}
                onClick={onStartEdit}
                title="Edit task title"
              >
                {task.task_number}. {task.title}
              </button>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {task.is_complete && (
                <StatusBadge tone="success">Complete</StatusBadge>
              )}
              {task.is_hidden && <Badge variant="outline">Hidden</Badge>}
              {task.client_title && (
                <Badge variant="outline" className="max-w-full truncate">
                  Client: {task.client_title}
                </Badge>
              )}
              {assignments.map((assignment) => (
                <Badge key={assignment.user_id} variant="secondary">
                  {[assignment.user.firstname, assignment.user.lastname]
                    .filter(Boolean)
                    .join(" ")}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {hasMeetingDetails && (
          <div className="mt-4 grid gap-2 rounded-md border bg-muted/20 p-3 text-sm sm:grid-cols-2">
            <SummaryField label="Date" value={meetingDetails.date || "-"} />
            <SummaryField label="Time" value={meetingDetails.time || "-"} />
            <SummaryField
              label="Attendees"
              value={meetingDetails.attendees || "None"}
            />
            <SummaryField
              label="Link"
              value={
                meetingDetails.link ? (
                  <span className="break-all">{meetingDetails.link}</span>
                ) : (
                  "-"
                )
              }
            />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-start justify-between gap-2 lg:justify-end">
        <div className="flex flex-wrap justify-start gap-2 lg:justify-end">
          {canAssign && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  <Users className="h-4 w-4" />
                  Owners{assignments.length ? ` (${assignments.length})` : ""}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Task owners</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {staff.length ? (
                  staff.map((participant) => {
                    const assigned = assignments.some(
                      (assignment) => assignment.user_id === participant.user_id,
                    );
                    return (
                      <DropdownMenuCheckboxItem
                        key={participant.user_id}
                        checked={assigned}
                        disabled={assignmentBusy === `${task.id}:${participant.user_id}`}
                        onSelect={(event) => event.preventDefault()}
                        onCheckedChange={() => onOwnerChange(participant.user_id, assigned)}
                      >
                        {[participant.user.firstname, participant.user.lastname]
                          .filter(Boolean)
                          .join(" ")}
                      </DropdownMenuCheckboxItem>
                    );
                  })
                ) : (
                  <p className="px-2 py-3 text-sm text-muted-foreground">
                    Add staff to the project team first.
                  </p>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={task.included_in_package !== false} onChange={e => onPackageChange("included_in_package", e.target.checked)} />Included in package</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!task.visible_to_client} onChange={e => onPackageChange("visible_to_client", e.target.checked)} />Visible to client</label>
          {actions}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onHide}
          disabled={!!task.is_complete}
          title={
            task.is_complete
              ? "Task complete"
              : task.is_hidden
                ? "Show task"
                : "Hide task"
          }
          aria-label={task.is_hidden ? "Show task" : "Hide task"}
        >
          {task.is_hidden ? (
            <Eye className="h-4 w-4" />
          ) : (
            <EyeOff className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  </div>
);

const TaskActionButton = ({ children, onClick }) => (
  <Button type="button" variant="outline" size="sm" onClick={onClick}>
    <FileText className="h-4 w-4" />
    {children}
  </Button>
);

const SummaryField = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-xs font-medium uppercase text-muted-foreground">
      {label}
    </p>
    <div className="mt-1 break-words text-sm font-medium text-foreground">
      {value}
    </div>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
    {text}
  </div>
);

export default AgentTasks;
